import {
  createSkyTwinExecutioners,
  stepSkyTwinExecutioners,
} from './129-sky-twin-executioners.mjs';
import { createSkyVesper, stepSkyVesper, finishSkyVesper } from './125-sky-vesper.mjs';
import {
  createSkyScrapEmperor,
  stepSkyScrapEmperor,
  finishSkyScrapEmperor,
} from './124-sky-scrap-emperor.mjs';
import {
  createSkyHourglassKing,
  stepSkyHourglassKing,
  finishSkyHourglassKing,
} from './123-sky-hourglass-king.mjs';
import {
  createSkyIronOrchid,
  stepSkyIronOrchid,
  finishSkyIronOrchid,
} from './122-sky-iron-orchid.mjs';
import {
  createSkyMourningBride,
  stepSkyMourningBride,
  finishSkyMourningBride,
} from './121-sky-mourning-bride.mjs';
import {
  createSkyPrismWidow,
  stepSkyPrismWidow,
  finishSkyPrismWidow,
} from './120-sky-prism-widow.mjs';
import { createSkyCinderheart, stepSkyCinderheart } from './117-sky-cinderheart.mjs';
import { recordSkyCinderSupportImpact } from './128-sky-cinderheart-pose.mjs';
import {
  compileSkyAuthoredRig,
  sampleSkyAuthoredPose,
  skyAuthoredPoseContact,
} from './115-sky-authored-pose.mjs';
import { skyTerrainHeight } from './87-sky-terrain.mjs';

// Source geometry and authored clip sampling are shared; each signature owns
// its physical interaction. Missing prop readiness never manufactures progress.
const SPECS = Object.freeze({
  'twin-executioners': {
    target: 'reactor_target',
    emitter: 'gun_left',
    death: 'Defeat',
    height: 24,
    beats: [
      ['Idle', 4, false],
      ['PowerTransfer', 9, true],
      ['Repair', 3, false],
    ],
  },
  'scrap-emperor': {
    target: 'reactor_core',
    emitter: 'cannon_muzzle',
    death: 'Defeat',
    height: 2,
    beats: [
      ['Idle', 4, false],
      ['MagnetTell', 1.5, false],
      ['SalvageOrbit', 3, true],
      ['ShieldBuild', 2, false],
      ['CoreExpose', 2, true],
    ],
  },
  vesper: {
    target: 'resonator_left',
    emitter: 'sonic_emitter',
    death: 'Defeat',
    height: 12,
    beats: [
      ['Idle', 4, false],
      ['SonicTell', 1.5, false],
      ['SonicFire', 1, false],
      ['BankLeft', 5, true],
      ['BankRight', 5, true],
      ['Recover', 2, false],
    ],
  },
  'hourglass-king': {
    target: 'hourglass_core',
    emitter: 'crown_emitter',
    death: 'Defeat',
    height: 2,
    beats: [
      ['Idle', 4, false],
      ['AttackTell', 1.4, false],
      ['Fire', 1, false],
      ['RewindTell', 1.2, false],
      ['Rewind', 2, false],
    ],
  },
  'prism-widow': {
    target: 'abdomen_core',
    emitter: 'beam_emitter',
    death: 'Defeat',
    height: 8,
    beats: [
      ['Idle', 2, true],
      ['BeamCharge', 1.4166667, false],
      ['BeamSweep', 2, false, 'fan'],
      ['MirrorRecoil', 1.2083334, true],
    ],
  },
  cinderheart: {
    target: 'vent_00',
    emitter: 'molten_emitter',
    death: 'Defeat',
    height: 0,
    beats: [
      ['Idle', 2, true],
      ['VentCharge', 2, true],
      ['Erupt', 1, false, 'paired'],
      ['Idle', 3, true],
    ],
  },
  'mourning-bride': {
    target: 'Anchor_port_lower_target',
    emitter: 'Echo_launch_0',
    death: 'Breakup',
    height: 4,
    beats: [
      ['Glide', 3, false],
      ['AnchorReveal', 3, true],
      ['EchoLaunch', 2.4166667, false, 'paired'],
      ['Glide', 3, true],
    ],
  },
  'iron-orchid': {
    target: 'Reactor_target',
    emitter: 'Pollen_emitter',
    death: 'ReactorCollapse',
    height: 0,
    beats: [
      ['Track', 2, false],
      ['SpiralBloom', 4, true],
      ['PollenRelease', 4, false, 'fan'],
      ['Idle', 3, true],
    ],
  },
});
const specFor = (id) => (Object.hasOwn(SPECS, id) ? SPECS[id] : null);

/** Install a readiness-compiled, metre-scale rig into this run only. Returns
 * false for unsupported worlds, missing named clips/sockets or malformed data;
 * it never grants progress or changes an active encounter's physical mesh. */
export function installSkyAuthoredBossRig(state, source) {
  const rig = compileSkyAuthoredRig(source),
    spec = specFor(rig?.worldId);
  if (
    !state ||
    !rig ||
    !spec ||
    ![...spec.beats.map((beat) => beat[0]), spec.death].every((name) =>
      rig.clips.some((clip) => clip.name === name),
    ) ||
    ![spec.target, spec.emitter].every((name) => rig.sockets.some((socket) => socket.name === name))
  )
    return false;
  if (state.boss?.authoredRig?.worldId === rig.worldId && state.boss.authoredRig !== rig)
    return false;
  state.authoredBossRigs ??= {};
  state.authoredBossRigs[rig.worldId] = rig;
  return true;
}

/** Bind a freshly spawned guardian to the prepared original. Missing readiness
 * returns false, leaving callers responsible for their explicitly retained
 * diagnostic encounters. Runtime readiness requires this rig before launch. */
export function bindSkyAuthoredBoss(state, stage) {
  const rig = state.authoredBossRigs?.[stage.worldId],
    spec = specFor(stage.worldId),
    boss = state.boss;
  if (!boss || !rig || !spec) return false;
  Object.assign(boss, {
    authoredRig: rig,
    artVariant: stage.worldId,
    worldId: stage.worldId,
    name: stage.bossName,
    controlMode: 'steer',
    arenaMotion: 'fixed',
    attackKind: stage.worldId === 'cinderheart' ? 'fireball' : 'energy',
    age: 0,
    phaseAge: 0,
    collisionEnabled: true,
    authoredBeat: -1,
    authoredRehearsal: true,
    deathDuration: rig.clips.find((clip) => clip.name === spec.death).duration,
  });
  updatePose(state, boss, spec, spec.beats[0][0], 0, false);
  if (
    createSkyTwinExecutioners(state, boss) ||
    createSkyCinderheart(state, boss) ||
    createSkyPrismWidow(state, boss) ||
    createSkyMourningBride(state, boss) ||
    createSkyIronOrchid(state, boss) ||
    createSkyHourglassKing(state, boss) ||
    createSkyVesper(state, boss) ||
    createSkyScrapEmperor(state, boss)
  )
    boss.authoredRehearsal = false;
  return true;
}
function beatAt(spec, age) {
  const total = spec.beats.reduce((sum, beat) => sum + beat[1], 0),
    cycle = Math.floor(age / total);
  let remaining = age - cycle * total;
  for (let i = 0; i < spec.beats.length; i++) {
    const beat = spec.beats[i];
    if (remaining < beat[1] || i === spec.beats.length - 1)
      return { beat, index: cycle * spec.beats.length + i, age: remaining };
    remaining -= beat[1];
  }
}
function updatePose(state, boss, spec, clip, seconds, exposed, transitionAge = seconds) {
  const rig = boss.authoredRig,
    bounds = rig.bounds;
  const entry = Math.min(1, boss.age / 4),
    easing = entry * entry * (3 - 2 * entry);
  const lostAnchors = boss.mourning?.anchors.filter((anchor) => anchor.hp <= 0) ?? [];
  const list = lostAnchors.reduce((sum, anchor) => sum + (anchor.index < 2 ? -0.06 : 0.06), 0);
  const x = boss.orchid
    ? 0
    : (1 - lostAnchors.length * 0.16) *
      Math.sin(boss.age * (boss.prism ? 0.1 + boss.prism.severed.length * 0.025 : 0.19)) *
      (boss.prism ? 2 + boss.prism.severed.length : 2);
  const z = -32 + bounds.min[2] - (1 - easing) * 48;
  const y = spec.height - bounds.min[1] + skyTerrainHeight(x, z, state.terrainTravel ?? 0);
  boss.rootTransform = boss.scrap?.rootTransform ??
    boss.vesper?.rootTransform ??
    boss.orchid?.rootTransform ??
    boss.hourglass?.rootTransform ?? {
      position: [x, y, z],
      quaternion: [Math.sin(list / 2), Math.cos(list / 2), 0, 0],
      scale: [1, 1, 1],
    };
  const [physicalX, physicalY, physicalZ] = boss.rootTransform.position;
  Object.assign(boss, {
    x: physicalX,
    y: physicalY,
    z: physicalZ,
    vx: boss.scrap || boss.orchid ? 0 : Math.cos(boss.age * 0.19) * 0.38,
    vy: 0,
    vz: 0,
  });
  boss.authoredClip = clip;
  boss.authoredPose = sampleSkyAuthoredPose(rig, clip, seconds, boss.rootTransform, {
    pose: boss.authoredTransition,
    overrides:
      boss.defeated && (boss.mourning || boss.orchid || boss.hourglass)
        ? undefined
        : (boss.cinderheart?.frozenPose ??
          boss.prism?.overrides ??
          boss.mourning?.overrides ??
          boss.orchid?.overrides ??
          boss.hourglass?.overrides),
    weight: Math.max(0, 1 - transitionAge / 0.25),
  });
  recordSkyCinderSupportImpact(state, boss);
  const pose = boss.authoredPose,
    target = pose.sockets[spec.target],
    emitter = pose.sockets[spec.emitter];
  const size = pose.bounds.max.map((value, i) => value - pose.bounds.min[i]);
  const radius = Math.max(0.7, Math.min(2.5, Math.max(...size) * 0.035));
  const primary = {
    ...target,
    id: spec.target,
    label: spec.target.replaceAll('_', ' ').toUpperCase(),
    radius,
    criticalRadius: radius * 0.55,
    exposed,
  };
  // Preserve source metre scale and let the real pilot reach tall targets.
  // This is a physical steering envelope, never an invisible camera offset.
  boss.authoredFlightCeiling = Math.min(
    90,
    Math.max(boss.authoredFlightCeiling ?? 9, target.y + 3),
  );
  boss.weakpoint = primary;
  boss.weakpoints = [primary];
  boss.vulnerable = exposed;
  boss.attackSocket = { ...emitter };
  boss.body = {
    x: (pose.bounds.min[0] + pose.bounds.max[0]) / 2,
    y: (pose.bounds.min[1] + pose.bounds.max[1]) / 2,
    z: pose.bounds.max[2],
    radius: Math.max(...size) / 2,
  };
  boss.anatomy = { primary, targets: [primary], body: boss.body, attackSocket: boss.attackSocket };
}

/** Advance real clip-driven tells and finite shooting/recovery phases. Only
 * active simulation dt advances source articulation or emitted aimed volleys.
 * The existing projectile owner resolves damage; no socket bypasses geometry. */
export function stepSkyAuthoredBoss(state, dt, { event, fireAimed, hit }) {
  const boss = state.boss,
    spec = specFor(boss?.worldId);
  if (!boss?.authoredRig || !spec || !(dt >= 0) || !Number.isFinite(dt)) return false;
  if (boss.hp <= 0 && !boss.defeated) return true;
  boss.age += dt;
  if (boss.twins) return stepSkyTwinExecutioners(state, dt, { event, fireAimed, hit });
  if (boss.defeated) {
    if (boss.authoredClip !== spec.death) boss.authoredTransition = boss.authoredPose;
    updatePose(state, boss, spec, spec.death, boss.deathAge ?? 0, false);
    return true;
  }
  if (boss.scrap) {
    const beat = stepSkyScrapEmperor(state, dt, { event });
    updatePose(state, boss, spec, beat.clip, beat.seconds, beat.exposed);
    boss.attackPhase = boss.scrap.phase;
    boss.phaseAge = boss.scrap.age;
    finishSkyScrapEmperor(state, dt, { event, fireAimed });
    return true;
  }
  if (boss.vesper) {
    const beat = stepSkyVesper(state, dt, { event });
    updatePose(
      state,
      boss,
      { ...spec, target: beat.target },
      beat.clip,
      beat.seconds,
      beat.exposed,
      beat.transitionAge,
    );
    boss.attackPhase = boss.vesper.phase;
    boss.phaseAge = boss.vesper.age;
    finishSkyVesper(state, dt, { event, hit });
    return true;
  }
  if (boss.hourglass) {
    const beat = stepSkyHourglassKing(state, dt, { event });
    updatePose(
      state,
      boss,
      { ...spec, target: beat.target },
      beat.clip,
      beat.seconds,
      beat.exposed,
    );
    boss.attackPhase = boss.hourglass.phase;
    boss.phaseAge = boss.hourglass.age;
    finishSkyHourglassKing(state, dt, { event, fireAimed });
    return true;
  }
  if (boss.orchid) {
    const beat = stepSkyIronOrchid(state, dt, { event, hit });
    updatePose(
      state,
      boss,
      { ...spec, target: beat.target },
      beat.clip,
      beat.seconds,
      beat.exposed,
    );
    boss.attackPhase = boss.orchid.phase;
    boss.phaseAge = boss.orchid.age;
    boss.authoredFlightCeiling = Math.min(
      90,
      Math.max(boss.authoredFlightCeiling, boss.authoredPose.bounds.max[1] + 6),
    );
    finishSkyIronOrchid(state, dt, { event, hit });
    return true;
  }
  if (boss.mourning) {
    const beat = stepSkyMourningBride(state, dt, { event, hit });
    updatePose(
      state,
      boss,
      { ...spec, target: beat.target },
      beat.clip,
      beat.seconds,
      beat.exposed,
    );
    boss.attackPhase = boss.mourning.phase;
    boss.phaseAge = boss.mourning.age;
    finishSkyMourningBride(state, dt, { event, hit });
    return true;
  }
  if (boss.prism) {
    const beat = stepSkyPrismWidow(state, dt, { event });
    updatePose(state, boss, spec, beat.clip, beat.seconds, beat.exposed);
    boss.attackPhase = boss.prism.phase;
    boss.phaseAge = boss.prism.age;
    boss.charge = boss.attackPhase === 'charge' ? Math.min(1, boss.phaseAge / 1.4166667) : 0;
    finishSkyPrismWidow(state, dt, { event, hit });
    return true;
  }
  if (boss.cinderheart) {
    const beat = stepSkyCinderheart(state, dt, { event, fireAimed });
    updatePose(
      state,
      boss,
      { ...spec, target: beat.target },
      beat.clip,
      beat.seconds,
      beat.exposed,
    );
    boss.attackPhase = boss.cinderheart.phase;
    boss.phaseAge = boss.cinderheart.age;
    boss.charge = boss.attackPhase === 'charge' ? Math.min(1, boss.phaseAge / 2) : 0;
    if (['rolling', 'exposed'].includes(boss.attackPhase))
      boss.authoredFlightCeiling = Math.min(
        90,
        Math.max(boss.authoredFlightCeiling, boss.authoredPose.bounds.max[1] + 6),
      );
    if (boss.attackPhase === 'charge') {
      boss.weakpoints = Array.from({ length: 8 }, (_, i) => ({
        ...boss.weakpoint,
        ...boss.authoredPose.sockets[`vent_0${i}`],
        id: `vent_0${i}`,
      }));
      boss.anatomy.targets = boss.weakpoints;
    }
    return true;
  }
  const ready = boss.age >= 4,
    beat = beatAt(spec, Math.max(0, boss.age - 4));
  const [clip, duration, exposed, pattern] = beat.beat;
  if (ready && boss.authoredBeat !== beat.index) {
    boss.authoredTransition = boss.authoredPose;
    boss.authoredBeat = beat.index;
    boss.authoredShots = 0;
    event(
      state,
      'boss-phase',
      pattern ? 'GUARDIAN VOLLEY · DODGE' : exposed ? 'EXPOSED SURFACE · FIRE' : 'WATCH THE CHARGE',
    );
  }
  boss.phaseAge = beat.age;
  boss.combatPhase = 1 + Math.min(2, Math.floor((1 - boss.hp / boss.maxHp) * 3));
  boss.attackPhase = !ready ? 'approach' : pattern ? 'firing' : exposed ? 'exposed' : 'charge';
  boss.charge = pattern ? 1 : !exposed ? Math.min(1, beat.age / duration) : 0;
  const source = boss.authoredRig.clips.find((candidate) => candidate.name === clip);
  updatePose(state, boss, spec, clip, Math.min(source.duration, beat.age), ready && exposed);
  if (ready && pattern) {
    const due = Math.min(3, Math.floor(beat.age / Math.max(0.6, duration / 3)) + 1);
    while ((boss.authoredShots ?? 0) < due) {
      fireAimed(state, boss.attackSocket, pattern, boss);
      boss.authoredShots = (boss.authoredShots ?? 0) + 1;
    }
  }
  return true;
}

/** Resolve against the same posed triangles the renderer consumes. Precision
 * classifies the nearest visible surface by3D distance to its authored socket;
 * a hidden rear core cannot turn a hit on front armor into a critical strike. */
export function skyAuthoredBossContact(boss, from, to) {
  const hit = skyAuthoredPoseContact(boss.authoredPose, from, to);
  if (!hit) return null;
  const weak = boss.weakpoints?.find(
    (target) => Math.hypot(hit.x - target.x, hit.y - target.y, hit.z - target.z) <= target.radius,
  );
  return {
    ...hit,
    region: weak ? 'core' : 'armor',
    weakpointId: weak?.id,
    authoredCritical: Boolean(
      weak && Math.hypot(hit.x - weak.x, hit.y - weak.y, hit.z - weak.z) <= weak.criticalRadius,
    ),
  };
}
